import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';

/**
 * NextAuth session type for React components.
 * Contains only user information needed for UI.
 */
interface Session {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    id?: string | null;
  };
  expires: string;
}

interface AuthContextType {
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth provider that manages NextAuth session state for the React app.
 *
 * Fetches session from NextAuth's /api/auth/session endpoint and provides
 * authentication status to all components via React Context.
 *
 * Features:
 * - Automatic session fetching on mount
 * - Manual refresh via refreshSession()
 * - Provides isAuthenticated boolean for conditional rendering
 *
 * Usage:
 * ```tsx
 * const { isAuthenticated, session } = useAuth();
 * if (isAuthenticated) {
 *   // Show authenticated content
 * }
 * ```
 */
function isValidSession(data: any): boolean {
  return Boolean(data && Object.keys(data).length > 0 && data.user?.email);
}

function isTokenRefreshError(data: any): boolean {
  return data?.error === 'RefreshAccessTokenError';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isFetching = useRef(false);

  const handleSessionData = (data: any) => {
    if (isValidSession(data)) {
      setSession(data);
      return;
    }
    setSession(null);
    if (isTokenRefreshError(data)) {
      console.warn('Token refresh failed, redirecting to sign in');
      window.location.href = '/api/auth/signin';
    }
  };

  const fetchSession = async () => {
    // Prevent concurrent fetches to avoid race conditions
    if (isFetching.current) return;
    isFetching.current = true;

    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        handleSessionData(await response.json());
      } else {
        setSession(null);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      setSession(null);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  };

  // Fetch session on mount
  useEffect(() => {
    fetchSession(); // Initial fetch
  }, []);

  const refreshSession = async () => {
    setIsLoading(true);
    await fetchSession();
  };

  const value: AuthContextType = {
    session,
    isAuthenticated: session !== null,
    isLoading,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
